import type { TranslatedString } from '@interfaces/shared';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { FlagsmithFlags } from '@utils/flagsmith';
import { branchKey } from '@utils/workflowBranchKey';
import { Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  isChoiceEnabled,
  modelPortfolioEditChoices,
} from './modelPortfolioEditChoices';

export type SelectModelPortfolioEditTypeOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
} & Record<string, unknown>;

export type SelectModelPortfolioEditTypeVisualProps = {
  options: SelectModelPortfolioEditTypeOptions;
  loading?: boolean;
  householdAccountCount?: number;
  drawnBranchKeys?: string[];
  /** Receives the branch key of the chosen edit type. */
  onNext: (branchKey: string) => void | Promise<void>;
};

export const SelectModelPortfolioEditTypeVisual = ({
  options,
  loading = false,
  householdAccountCount,
  drawnBranchKeys,
  onNext,
}: SelectModelPortfolioEditTypeVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const customizedAllocationsEnabled = useFeatureFlag(
    FlagsmithFlags.household_portfolio_customized_allocations_workflow,
  );
  const [selectedBranchKey, setSelectedBranchKey] = useState('');
  // An undrawn choice fails with "There is no next sub step", and on steps saved
  // before that choice existed it has no label either, so it shows as a blank tile.
  const isBranchDrawn = (value: string) =>
    !drawnBranchKeys?.length || drawnBranchKeys.includes(value);

  const choices = modelPortfolioEditChoices
    .map((choice, index) => {
      const isUnavailableForSingleAccount =
        choice.requiresMultipleAccounts && householdAccountCount === 1;

      return {
        value: branchKey(index),
        label: translateBackendString(options?.[choice.labelKey]),
        subtitle: translateBackendString(options?.[choice.descriptionKey]),
        enabled:
          isChoiceEnabled(options, choice.enableKey) &&
          (customizedAllocationsEnabled ||
            !choice.requiresCustomizedAllocationsWorkflow),
        disabled: isUnavailableForSingleAccount,
        helpText: isUnavailableForSingleAccount
          ? t('selectModelPortfolioEditType.customizedSingleAccountTooltip')
          : undefined,
      };
    })
    .filter((choice) => choice.enabled && isBranchDrawn(choice.value));

  const selectedChoice = choices.find(
    (choice) => choice.value === selectedBranchKey,
  );
  const activeBranchKey =
    selectedChoice && !selectedChoice.disabled ? selectedBranchKey : '';

  return (
    <WorkflowCompletionForm
      onSubmit={() => onNext(activeBranchKey)}
      testId='select-model-portfolio-edit-type-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: !activeBranchKey || loading,
        dataTestId: 'select-model-portfolio-edit-type-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      <SelectionTile
        testId='model-portfolio-edit-type'
        value={activeBranchKey}
        onChange={(event: { target: { value: string } }) =>
          setSelectedBranchKey(event.target.value)
        }
        options={choices}
      />
    </WorkflowCompletionForm>
  );
};

export default SelectModelPortfolioEditTypeVisual;
